'use client';

import type { ReactNode } from 'react';
import { Toast } from '@base-ui/react/toast';
import { X } from 'lucide-react';

export type ToastData = {
  traceId?: string;
};

/** Same auto-dismiss the previous queue used. */
const TOAST_DURATION_MS = 6000;

const VARIANT_STYLES: Record<string, string> = {
  error: 'border-red-300 bg-red-50 text-red-900',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  info: 'border-blue-300 bg-blue-50 text-blue-900',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    // Previous queue had no cap. Base UI's default of 3 marks older toasts inert.
    <Toast.Provider timeout={TOAST_DURATION_MS} limit={Infinity}>
      {children}
      <ToastViewport />
    </Toast.Provider>
  );
}

function ToastViewport() {
  const { toasts } = Toast.useToastManager<ToastData>();
  if (toasts.length === 0) return null;

  return (
    <Toast.Viewport className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((item) => (
        <Toast.Root
          key={item.id}
          toast={item}
          swipeDirection={[]}
          className={`animate-in slide-in-from-right-full fade-in rounded-lg border px-4 py-3 shadow-lg text-sm ${VARIANT_STYLES[item.type ?? 'error'] ?? VARIANT_STYLES.error}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Toast.Title className="m-0 text-sm font-medium" />
              <Toast.Description className="mt-1 opacity-80" />
              {item.data?.traceId ? (
                <p className="mt-1 font-mono text-[10px] opacity-60">trace: {item.data.traceId}</p>
              ) : null}
            </div>
            <Toast.Close
              className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
            >
              <X size={14} />
            </Toast.Close>
          </div>
        </Toast.Root>
      ))}
    </Toast.Viewport>
  );
}
